import { Controller } from '@hotwired/stimulus';

/**
 * Auto-Download Controller
 *
 * Starts a file download EXACTLY ONCE per browser tab session, keyed by the
 * download URL (which embeds the export id).
 *
 * The download is a one-shot side effect, but `connect()` is a lifecycle hook
 * that re-fires on every DOM entry: the in-page render burst (initial render +
 * the modal's one-shot reconcile fetch + the live broadcast replace) AND Turbo
 * Drive cache / back-forward restoration (which remounts every controller in the
 * restored snapshot). Idempotency keyed by the URL — not a timing window — is
 * what makes that safe. (A time-window debounce used to guard this; it re-fired
 * the download whenever the user left the page and returned after the window.)
 *
 * The marker is persisted in `sessionStorage` so it survives Turbo navigations
 * within the tab; an in-memory Set is the fallback for browsers that throw on
 * `sessionStorage` (e.g. some private-mode configs), mirroring
 * `local_storage_dismiss_controller`.
 *
 * A deliberate re-export always creates a NEW Export (a `ready` export is never
 * reused — see ExportsController#find_reusable_export), so it gets a new id → new
 * URL → new key → downloads normally. The visible fallback link in the `_ready`
 * partial covers any case where the browser blocks the programmatic navigation.
 *
 * Values:
 * - url: The download URL to navigate to.
 *
 * @example
 * <div data-controller="auto-download"
 *      data-auto-download-url-value="/exports/42/download">
 * </div>
 */
const firedInMemory = new Set();

export default class extends Controller {
  static values = { url: String };

  connect() {
    if (!this.urlValue || this.#alreadyFired()) return;

    this.#markFired();
    window.location.href = this.urlValue;
  }

  #storageKey() {
    return `auto-download:${this.urlValue}`;
  }

  #alreadyFired() {
    try {
      return sessionStorage.getItem(this.#storageKey()) === '1';
    } catch {
      return firedInMemory.has(this.urlValue);
    }
  }

  #markFired() {
    try {
      sessionStorage.setItem(this.#storageKey(), '1');
    } catch {
      firedInMemory.add(this.urlValue);
    }
  }
}
